import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import ProviderHubGenerateReportFlyout from "@app/projects/network-assessment/provider-hub/ProviderHubGenerateReportFlyout";
import { ProviderHubBasicFilters } from "@app/projects/network-assessment/provider-hub/ProvidersHubBasicFilters";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { formatGapImpactCount, formatSpecialtyWithCode, SortContractEntityByOptionToDisplay, NO_ACTIONS_APPLIED_LABEL } from "@app/projects/network-assessment/provider-hub/utility";
import { ProviderHubContractEntitySortColumn, ProviderHubContractEntities, ProviderHubContractEntityTableData } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import { ShowFullDataToggle } from "@app/projects/network-assessment/provider-hub/ShowFullDataToggle";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";

// Gap Impact "Sort by" options: the single combined option pair vs. the four region-status pairs.
// The dropdown swaps between these based on whether the region-status breakdown is being displayed.
const combinedGapImpactSortOptions: ProviderHubContractEntitySortColumn[] = [
  ProviderHubContractEntitySortColumn.GapsClosedDescending,
  ProviderHubContractEntitySortColumn.GapsClosedAscending,
];

const breakdownGapImpactSortOptions: ProviderHubContractEntitySortColumn[] = [
  ProviderHubContractEntitySortColumn.ActiveGapsClosedDescending,
  ProviderHubContractEntitySortColumn.ActiveGapsClosedAscending,
  ProviderHubContractEntitySortColumn.PendingGapsClosedDescending,
  ProviderHubContractEntitySortColumn.PendingGapsClosedAscending,
  ProviderHubContractEntitySortColumn.ApprovedGapsClosedDescending,
  ProviderHubContractEntitySortColumn.ApprovedGapsClosedAscending,
  ProviderHubContractEntitySortColumn.OpportunityGapsClosedDescending,
  ProviderHubContractEntitySortColumn.OpportunityGapsClosedAscending,
];

// Every Gap Impact sort column (combined + breakdown) — used to keep them out of the "Other" optgroup.
const gapImpactSortColumns = new Set<ProviderHubContractEntitySortColumn>([
  ...combinedGapImpactSortOptions,
  ...breakdownGapImpactSortOptions,
]);

const ProviderHubContractEntityView: FC = () => {
  const projectContext = useContext(CountyProjectContext);
  const providerHubContractEntityContext = useContext(ProviderHubContext);
  const providerHubService = useRef(new ProviderHubService()).current;


  const showPci = providerHubContractEntityContext.showPciFilters;
  const showMotive = projectContext.project.showMotiveAppropriatenessScore;
  const showCareJourney = projectContext.project.showCareJourneyScores;

  // PCI/Motive/CareJourney sort options only appear when the corresponding data is licensed and enabled.
  const pciSortOptions = new Set<ProviderHubContractEntitySortColumn>([
    ProviderHubContractEntitySortColumn.FilteredQualityDescending,
    ProviderHubContractEntitySortColumn.FilteredQualityAscending,
    ProviderHubContractEntitySortColumn.FilteredCostDescending,
    ProviderHubContractEntitySortColumn.FilteredCostAscending
  ]);
  const motiveSortOptions = new Set<ProviderHubContractEntitySortColumn>([
    ProviderHubContractEntitySortColumn.FilteredMotiveAppropriatenessDescending,
    ProviderHubContractEntitySortColumn.FilteredMotiveAppropriatenessAscending
  ]);
  const careJourneySortOptions = new Set<ProviderHubContractEntitySortColumn>([
    ProviderHubContractEntitySortColumn.FilteredCareJourneyCostDescending,
    ProviderHubContractEntitySortColumn.FilteredCareJourneyCostAscending,
    ProviderHubContractEntitySortColumn.FilteredCareJourneyQualityDescending,
    ProviderHubContractEntitySortColumn.FilteredCareJourneyQualityAscending
  ]);

  const isSortOptionVisible = (option: ProviderHubContractEntitySortColumn): boolean => {
    if (option === ProviderHubContractEntitySortColumn.Unknown) return false;
    if (pciSortOptions.has(option)) return showPci;
    if (motiveSortOptions.has(option)) return showMotive;
    if (careJourneySortOptions.has(option)) return showCareJourney;
    return true;
  };

  // Show the four-column region-status breakdown when the backend reports that this run carries region-status
  // data; otherwise fall back to the single Gap Impact column. These indicators are result-set-level metadata
  // (they cover every page), so paging never changes which columns or Gap Impact sort options are available.
  // The filtered and unfiltered ("Show Full Entity Data") columns are gated independently.
  const useRegionStatusBreakdown = providerHubContractEntityContext.providerHubContractEntities?.useGapsByRegionStatus ?? false;
  const unfilteredUseRegionStatusBreakdown = providerHubContractEntityContext.providerHubContractEntities?.unfilteredUseGapsByRegionStatus ?? false;

  const gapImpactSortOptions = useMemo<ProviderHubContractEntitySortColumn[]>(
    () => useRegionStatusBreakdown ? breakdownGapImpactSortOptions : combinedGapImpactSortOptions,
    [useRegionStatusBreakdown]
  );

  const otherSortOptions = useMemo<ProviderHubContractEntitySortColumn[]>(
    () => Object.values(ProviderHubContractEntitySortColumn).filter((option) => isSortOptionVisible(option) && !gapImpactSortColumns.has(option)),
    [showPci, showMotive, showCareJourney]
  );

  const sortByOptions = useMemo<ProviderHubContractEntitySortColumn[]>(
    () => [...gapImpactSortOptions, ...otherSortOptions],
    [gapImpactSortOptions, otherSortOptions]
  );

  /* oxlint-disable sort-keys */
  const filteredProviderHubContractEntitiesTableColumnConfig: ColumnConfig = {
    nameElement: {
      displayText: "Name",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_contract-entity-table-provider-name",
      noSort: true
    },
    entityType: {
      displayText: "Entity Type",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_contract-entity-table-entitytype",
      noSort: true
    },
    filteredProviderSources: {
      displayText: "Provider Sources",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-provider-source",
      tooltip: "Indicates the original database from which the provider data was obtained",
      noSort: true
    },
    filteredStatuses: {
      displayText: "Statuses",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-status",
      tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a contract entity",
      noSort: true
    },
    gapsClosed: {
      displayText: "Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes",
      noSort: true,
      hidden: useRegionStatusBreakdown
    },
    activeGapImpact: {
      displayText: "Active Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-active-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in active service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    pendingGapImpact: {
      displayText: "Pending Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-pending-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in pending service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    approvedGapImpact: {
      displayText: "Approved Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-approved-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in approved service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    opportunityGapImpact: {
      displayText: "Opportunity Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-opportunity-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in opportunity-to-expand service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    filteredServicingProviders: {
      displayText: "Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-servicingproviders",
      noSort: true
    },
    filteredServicingSpecialties: {
      displayText: "Servicing Specialty Groups",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-specialties",
      tooltip: "Indicates the specialty groups that the contract entity services",
      noSort: true
    },
    filteredOutOfNetworkServicingProviders: {
      displayText: "Out-of-network Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-oonsp",
      noSort: true
    },
    filteredInNetworkServicingProviders: {
      displayText: "In-network Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-insp",
      noSort: true
    },
    filteredQuality: {
      displayText: "Effectiveness (Quality)",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-quality",
      noSort: true,
      hidden: projectContext.project.showPciData === false
    },
    filteredCost: {
      displayText: "Efficiency (Cost)",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-cost",
      noSort: true,
      hidden: projectContext.project.showPciData === false
    },
    filteredMotiveAppropriateness: {
      displayElement: <ProviderScoreColumnHeader variant="motive" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-motive-appropriateness",
      noSort: true,
      hidden: projectContext.project.showMotiveAppropriatenessScore === false
    },
    filteredCareJourneyCost: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-care-journey-cost",
      noSort: true,
      hidden: projectContext.project.showCareJourneyScores === false
    },
    filteredCareJourneyQuality: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-care-journey-quality",
      noSort: true,
      hidden: projectContext.project.showCareJourneyScores === false
    }
  }

  const unfilteredProviderHubContractEntitiesTableColumnConfig: ColumnConfig = {
    nameElement: {
      displayText: "Name",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_contract-entity-table-provider-name",
      noSort: true
    },
    entityType: {
      displayText: "Entity Type",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_contract-entity-table-entitytype",
      noSort: true
    },
    filteredProviderSources: {
      displayText: "Provider Sources",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-provider-source",
      tooltip: "Indicates the original database from which the provider data was obtained",
      noSort: true
    },
    unfilteredProviderSources: {
      displayText: "Provider Sources - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-provider-source-unfiltered",
      tooltip: "Indicates the original database from which the provider data was obtained",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true
    },
    filteredStatuses: {
      displayText: "Statuses",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-status",
      tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a contract entity",
      noSort: true
    },
    unfilteredStatuses: {
      displayText: "Statuses - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-status-unfiltered",
      tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a contract entity",
      noSort: true,
      style: {backgroundColor: "#F4F9FF"},
    },
    gapsClosed: {
      displayText: "Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes",
      noSort: true,
      hidden: useRegionStatusBreakdown
    },
    unfilteredGapsClosed: {
      displayText: "Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-gap-impact-unfiltered",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: unfilteredUseRegionStatusBreakdown
    },
    activeGapImpact: {
      displayText: "Active Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-active-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in active service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredActiveGapImpact: {
      displayText: "Active Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-active-gap-impact-unfiltered",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in active service area counties",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !unfilteredUseRegionStatusBreakdown
    },
    pendingGapImpact: {
      displayText: "Pending Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-pending-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in pending service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredPendingGapImpact: {
      displayText: "Pending Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-pending-gap-impact-unfiltered",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in pending service area counties",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !unfilteredUseRegionStatusBreakdown
    },
    approvedGapImpact: {
      displayText: "Approved Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-approved-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in approved service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredApprovedGapImpact: {
      displayText: "Approved Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-approved-gap-impact-unfiltered",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in approved service area counties",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !unfilteredUseRegionStatusBreakdown
    },
    opportunityGapImpact: {
      displayText: "Opportunity Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-opportunity-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in opportunity-to-expand service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredOpportunityGapImpact: {
      displayText: "Opportunity Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-opportunity-gap-impact-unfiltered",
      tooltip: "Indicates the distinct number of gaps that the contract entity closes in opportunity-to-expand service area counties",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !unfilteredUseRegionStatusBreakdown
    },
    filteredServicingProviders: {
      displayText: "Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-servicingproviders",
      noSort: true
    },
    unfilteredServicingProviders: {
      displayText: "Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-servicingproviders",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true
    },
    filteredServicingSpecialties: {
      displayText: "Servicing Specialty Groups",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-specialties",
      tooltip: "Indicates the specialty groups that the contract entity services",
      noSort: true
    },
    unfilteredServicingSpecialties: {
      displayText: "Servicing Specialty Groups - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-specialties",
      tooltip: "Indicates the specialty groups that the contract entity services",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true
    },
    filteredOutOfNetworkServicingProviders: {
      displayText: "Out-of-network Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-oonsp",
      noSort: true
    },
    unfilteredOutOfNetworkServicingProviders: {
      displayText: "Out-of-network Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-oonsp",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true
    },
    filteredInNetworkServicingProviders: {
      displayText: "In-network Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-insp",
      noSort: true
    },
    unfilteredInNetworkServicingProviders: {
      displayText: "In-network Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-insp",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true
    },
    filteredQuality: {
      displayText: "Effectiveness (Quality)",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-quality",
      noSort: true,
      hidden: projectContext.project.showPciData === false
    },
    unfilteredQuality: {
      displayText: "Effectiveness (Quality) - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-quality",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: projectContext.project.showPciData === false
    },
    filteredCost: {
      displayText: "Efficiency (Cost)",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-cost",
      noSort: true,
      hidden: projectContext.project.showPciData === false
    },
    unfilteredCost: {
      displayText: "Efficiency (Cost) - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-cost",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: projectContext.project.showPciData === false
    },
    filteredMotiveAppropriateness: {
      displayElement: <ProviderScoreColumnHeader variant="motive" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-motive-appropriateness",
      noSort: true,
      hidden: projectContext.project.showMotiveAppropriatenessScore === false
    },
    unfilteredMotiveAppropriateness: {
      displayElement: <ProviderScoreColumnHeader variant="motive" label="Motive Appropriateness - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-motive-appropriateness",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: projectContext.project.showMotiveAppropriatenessScore === false
    },
    filteredCareJourneyCost: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-care-journey-cost",
      noSort: true,
      hidden: projectContext.project.showCareJourneyScores === false
    },
    unfilteredCareJourneyCost: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" label="CareJourney Cost - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-care-journey-cost",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: projectContext.project.showCareJourneyScores === false
    },
    filteredCareJourneyQuality: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-filtered-care-journey-quality",
      noSort: true,
      hidden: projectContext.project.showCareJourneyScores === false
    },
    unfilteredCareJourneyQuality: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" label="CareJourney Quality - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_contract-entity-table-unfiltered-care-journey-quality",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: projectContext.project.showCareJourneyScores === false
    }
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => handlePageChange(pageNumber),
    enableBackendPagination: true,
    handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
    initialPage: providerHubContractEntityContext.pageIndex,
    pageSizeOptions: [50, 100, 150],
    recordsPerPage: providerHubContractEntityContext.pageSize,
    resetToFirstPage: providerHubContractEntityContext.resetToFirstPage,
    showPageSizeSelector: true,
    totalRecords: providerHubContractEntityContext.providerHubContractEntities.totalCount
  }
  /* oxlint-enable sort-keys */

  const handlePageSizeChange = (pageSize: number) => {
    providerHubContractEntityContext.setPageSize(pageSize);
    providerHubContractEntityContext.setPageIndex(1);
  }

  const handlePageChange = async (newPage: number) => {
    if (!providerHubContractEntityContext.resetToFirstPage) {
      providerHubContractEntityContext.setPageIndex(newPage);
    }
    if (providerHubContractEntityContext.resetToFirstPage) {
      providerHubContractEntityContext.setResetToFirstPage(false);
    }
  };

  const [sortChangedByDropdown, setSortChangedByDropdown] = useState(false);
  const [showFullEntityData, setShowFullEntityData] = useState(false);

  const handleSortChange = (newSortValue: ProviderHubContractEntitySortColumn) => {
    providerHubContractEntityContext.setFilter('sortContractEntityColumnAndOrder', newSortValue)
    setSortChangedByDropdown(true);
  };

  useEffect(() => {
    if (sortChangedByDropdown) {
      providerHubContractEntityContext.handleSearchClick();
    }
    setSortChangedByDropdown(false);
  }, [providerHubContractEntityContext.filterState.sortContractEntityColumnAndOrder, sortChangedByDropdown]);

  // When the display mode flips (combined <-> breakdown) the previously selected Gap Impact sort may
  // no longer appear in the dropdown; reset to the default and re-search so the results reflect the
  // reset sort, not just the dropdown label.
  useEffect(() => {
    const currentSort = providerHubContractEntityContext.filterState.sortContractEntityColumnAndOrder;
    if (!sortByOptions.includes(currentSort)) {
      providerHubContractEntityContext.setFilter('sortContractEntityColumnAndOrder', ProviderHubContractEntitySortColumn.NameAscending);
      setSortChangedByDropdown(true);
    }
  }, [sortByOptions]);

  const handleContractEntityClick = async (contractEntity: ProviderHubContractEntities) => {

    const selectedContractEntityDetails = await providerHubService.getContractEntityDetails(projectContext?.project?.projectId, contractEntity.projectRunConfigurationId, contractEntity.contractEntityId);
    providerHubContractEntityContext.navigateToContractEntityProfile(selectedContractEntityDetails, ProviderProfileReferrerEnum.ProviderHubLandingPage);
  }

  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);

  /* oxlint-disable sort-keys */
  const contractEntityTableData: ProviderHubContractEntityTableData[] = useMemo<ProviderHubContractEntityTableData[]>(() => {

    let data: ProviderHubContractEntityTableData[] = [];
    data = providerHubContractEntityContext.providerHubContractEntities.items.map((contractEntity) => {
      return {
        key: contractEntity.contractEntityId.toString(),
        nameElement: <QESButton
          qesType="link"
          qesTestId={`provider-hub-contractentity-view__provider--${contractEntity.name}`}
          onClick={() => { handleContractEntityClick(contractEntity) }}
          className="demibold"
        >
          {contractEntity.name ?? "---"}
        </QESButton>,
        entityType: contractEntity.entityType,
        filteredProviderSources: (
          <>
            {sortProviderNetworkTypes(contractEntity.providerNetworkTypes).map((networkType) =>
              <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
            )}
          </>
        ),
        unfilteredProviderSources: (
          <>
            {sortProviderNetworkTypes(contractEntity.unfilteredProviderNetworkTypes).map((networkType) =>
              <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
            )}
          </>
        ),
        filteredStatuses: contractEntity.providerActionStatuses.length > 0 ? (
          <>
            {contractEntity.providerActionStatuses.map((status, index) =>
              <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot}  key={index} status={status} />
            )}
          </>
        ) : (
          <>{NO_ACTIONS_APPLIED_LABEL}</>
        ),
        unfilteredStatuses: contractEntity.unfilteredProviderActionStatuses.length > 0 ? (
          <>
            {contractEntity.unfilteredProviderActionStatuses.map((status, index) =>
              <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />
            )}
          </>
        ) : (
          <>{NO_ACTIONS_APPLIED_LABEL}</>
        ),
        gapsClosed: formatGapImpactCount(contractEntity.gapsClosed),
        unfilteredGapsClosed: formatGapImpactCount(contractEntity.unfilteredGapsClosed),
        activeGapImpact: formatGapImpactCount(contractEntity.activeGapsClosed),
        unfilteredActiveGapImpact: formatGapImpactCount(contractEntity.unfilteredActiveGapsClosed),
        pendingGapImpact: formatGapImpactCount(contractEntity.pendingGapsClosed),
        unfilteredPendingGapImpact: formatGapImpactCount(contractEntity.unfilteredPendingGapsClosed),
        approvedGapImpact: formatGapImpactCount(contractEntity.approvedGapsClosed),
        unfilteredApprovedGapImpact: formatGapImpactCount(contractEntity.unfilteredApprovedGapsClosed),
        opportunityGapImpact: formatGapImpactCount(contractEntity.opportunityGapsClosed),
        unfilteredOpportunityGapImpact: formatGapImpactCount(contractEntity.unfilteredOpportunityGapsClosed),
        filteredServicingProviders: contractEntity.filteredServicingProviders,
        unfilteredServicingProviders: contractEntity.unfilteredServicingProviders,
        filteredServicingSpecialties: <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={contractEntity.filteredServicingSpecialties?.map(formatSpecialtyWithCode)}
          numberOfElementsPerRow={3} />,
        unfilteredServicingSpecialties: <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={contractEntity.unfilteredServicingSpecialties?.map(formatSpecialtyWithCode)}
          numberOfElementsPerRow={3} />,
        filteredOutOfNetworkServicingProviders: contractEntity.filteredOutOfNetworkServicingProviders,
        unfilteredOutOfNetworkServicingProviders: contractEntity.unfilteredOutOfNetworkServicingProviders,
        filteredInNetworkServicingProviders: contractEntity.filteredInNetworkServicingProviders,
        unfilteredInNetworkServicingProviders: contractEntity.unfilteredInNetworkServicingProviders,
        filteredQuality: contractEntity.filteredQuality?.toString() ?? '---',
        unfilteredQuality: contractEntity.unfilteredQuality?.toString() ?? '---',
        filteredCost: contractEntity.filteredCost?.toString() ?? '---',
        unfilteredCost: contractEntity.unfilteredCost?.toString() ?? '---',
        filteredMotiveAppropriateness: contractEntity.filteredMotiveAppropriateness ?? '---',
        unfilteredMotiveAppropriateness: contractEntity.unfilteredMotiveAppropriateness ?? '---',
        filteredCareJourneyCost: contractEntity.filteredCareJourneyCost ?? '---',
        unfilteredCareJourneyCost: contractEntity.unfilteredCareJourneyCost ?? '---',
        filteredCareJourneyQuality: contractEntity.filteredCareJourneyQuality ?? '---',
        unfilteredCareJourneyQuality: contractEntity.unfilteredCareJourneyQuality ?? '---',
      }
    });

    return data;

  }, [providerHubContractEntityContext.providerHubContractEntities]);
  /* oxlint-enable sort-keys */

  return (
    <PageSection>
      <>
        <ProviderHubBasicFilters />
        <div className="provider-hub-providers-table-info-header">
          <div>
            <h5>Contract Entities</h5>
            <p>View, select and action on contract entity listed below</p>
            <QESSelect
              allowEmpty={false}
              className="sort-by-option"
              testId="provider-hub__sort-by-select"
              label="Sort by"
              onChange={(e) => handleSortChange(e.target.value as ProviderHubContractEntitySortColumn)}
              value={providerHubContractEntityContext.filterState.sortContractEntityColumnAndOrder}
            >
              <optgroup label="Gap Impact">
                {gapImpactSortOptions.map((option) => (
                  <option key={option} value={option}>
                    {SortContractEntityByOptionToDisplay(option)}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Other">
                {otherSortOptions.map((option) => (
                  <option key={option} value={option}>
                    {SortContractEntityByOptionToDisplay(option)}
                  </option>
                ))}
              </optgroup>
            </QESSelect>
          </div>
          <div style={{ alignItems: "center", display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
            {contractEntityTableData.length > 0 && (
              <QESButton
                qesType="primary"
                qesTestId="provider-hub-contractentity-view__generate-report"
                className="mb-2"
                onClick={() => setIsGenerateReportModalOpen(true)}
              >
                Generate Report
              </QESButton>
            )}
          </div>
        </div>
        <ProviderHubGenerateReportFlyout
          isModalOpen={isGenerateReportModalOpen}
          setIsModalOpen={setIsGenerateReportModalOpen}
          filters={providerHubContractEntityContext.filterState}
        />
        <div style={{ alignItems: "center", display: "flex", gap: "3rem", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
            { providerHubContractEntityContext.showFilterToggle && (
              <ShowFullDataToggle
                label={"Show Full Entity Data"}
                tooltipText={"Entity or TIN display both filtered data based on search criteria and unfiltered entity data available"}
                active={showFullEntityData}
                onChange={() => {setShowFullEntityData(!showFullEntityData)}}
                testId={"provider-hub-contractentity-view_full-entity-toggle"}
              />
            )}
            <p style={{ margin: 0 }}>Contract Entities: {providerHubContractEntityContext.providerHubContractEntities?.totalCount?.toLocaleString() ?? 0}</p>
            </div>

        <Waffles<ProviderHubContractEntityTableData>
          columns={showFullEntityData? unfilteredProviderHubContractEntitiesTableColumnConfig : filteredProviderHubContractEntitiesTableColumnConfig}
          data={contractEntityTableData}
          totalNumberOfRecords={contractEntityTableData.length}
          enablePagination={true}
          emptyTableMessage="No matching contract entity results found. Please start a new search"
          fullWidth={true}
          loading={providerHubContractEntityContext.loading}
          paginationProps={paginationProps}
          testId="provider-hub__contractentity-table"
          variant="frozen-border"
        />
      </>
    </PageSection>
  )
}

export default ProviderHubContractEntityView;